/**
 * Mac Desktop API Service
 * Handles all API calls to the macOS desktop backend
 */

import axiosInstance from "../../../utils/axios";

/**
 * List all macOS desktops for the current user
 */
export const listMacDesktops = async () => {
  const response = await axiosInstance.get("macos");
  return response.data;
};

/**
 * Create a new macOS desktop
 * @param {Object} params - Desktop creation parameters
 * @param {string} params.template - Template/AMI to use
 * @param {string} [params.instanceType] - EC2 instance type
 * @param {number} [params.volumeSize] - Volume size in GB
 * @param {string} [params.userGroup] - User group for billing
 */
export const createMacDesktop = async (params) => {
  const response = await axiosInstance.post("macos", params);
  return response.data;
};

/**
 * Get a specific macOS desktop by ID
 * @param {string} id - Desktop ID
 */
export const getMacDesktop = async (id) => {
  const response = await axiosInstance.get(`macos/${id}`);
  return response.data;
};

/**
 * Delete a macOS desktop
 * @param {string} id - Desktop ID
 */
export const deleteMacDesktop = async (id) => {
  await axiosInstance.delete(`macos/${id}`);
};

/**
 * Perform an action on a macOS desktop
 * @param {string} id - Desktop ID
 * @param {string} action - Action to perform (start, stop, terminate)
 */
export const macDesktopAction = async (id, action) => {
  const response = await axiosInstance.post(`macos/${id}/action`, { action });
  return response.data;
};

/**
 * Get credentials for a macOS desktop
 * @param {string} id - Desktop ID
 */
export const getMacCredentials = async (id) => {
  const response = await axiosInstance.get(`macos/${id}/credentials`);
  return response.data;
};

/**
 * Poll desktop status until it reaches a target state
 * @param {string} id - Desktop ID
 * @param {string[]} targetStates - Array of states to wait for
 * @param {number} [timeout=300000] - Timeout in ms (default 5 min)
 * @param {number} [interval=5000] - Polling interval in ms
 * @returns {Promise<Object>} - Desktop object when target state reached
 */
export const waitForDesktopState = async (id, targetStates, timeout = 300000, interval = 5000) => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const desktop = await getMacDesktop(id);

    if (targetStates.includes(desktop.phase)) {
      return desktop;
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(`Timeout waiting for desktop to reach state: ${targetStates.join(", ")}`);
};

const macApi = {
  listMacDesktops,
  createMacDesktop,
  getMacDesktop,
  deleteMacDesktop,
  macDesktopAction,
  getMacCredentials,
  waitForDesktopState
};

export default macApi;
